import Animated, { Easing, LinearTransition, ReduceMotion } from 'react-native-reanimated';
import { useWatch, Controller } from 'react-hook-form';
import { MultiImagePicker } from 'app/design/primitives/multi-image-picker';
import { useFormContext } from 'app/hooks/form';
import { useScopedTranslation } from 'app/hooks/i18n';
import { usePopularItemsByCategory } from 'app/network/category';
import type { CreateListingDTO, NewListingSource } from 'app/types/listing';
import { useTrackNewListingSource } from 'app/network/listing';
import { useEffect } from 'react';
// import type { schema } from '../index';
import { ListingType } from 'app/types/listing';
import { useUISize } from 'app/utils/web-helpers';
import { type ImagePickerAsset } from 'expo-image-picker';

export function GeneralSection({
  isAnalyzing = false,
  compactEmptyState = false,
  preservePicturesOnTypeChange = false,
  newListingSource,
  onChange,
}: {
  isAnalyzing?: boolean;
  compactEmptyState?: boolean;
  preservePicturesOnTypeChange?: boolean;
  newListingSource?: NewListingSource;
  onChange?: (images: ImagePickerAsset[]) => void;
}) {
  const { t } = useScopedTranslation();
  const isSmallUI = useUISize().isSmallUI;
  const { data: quickOffersAvailable } = usePopularItemsByCategory();

  // const { control, setValue } = useFormContext<yup.InferType<typeof schema>>();
  const { control, setValue } = useFormContext<CreateListingDTO>(); // Workaround for https://github.com/react-hook-form/react-hook-form/issues/12916

  const watchedType = useWatch({ control, name: 'type' });

  useEffect(() => {
    if (!preservePicturesOnTypeChange && watchedType === ListingType.request) {
      setValue('picture', null);
      setValue('pictures', []);
    }
  }, [preservePicturesOnTypeChange, setValue, watchedType]);

  useEffect(() => {
    if (quickOffersAvailable === undefined) return;
    if (quickOffersAvailable.length === 0) {
      if (!watchedType) {
        setValue('type', ListingType.offer);
      }
    }
  }, [quickOffersAvailable, setValue, watchedType]);

  const trackNewListingSource = useTrackNewListingSource();
  useEffect(() => {
    if (newListingSource) {
      trackNewListingSource(newListingSource);
    }
  }, [newListingSource, trackNewListingSource]);

  return (
    <Animated.View
      layout={LinearTransition.duration(280).easing(Easing.out(Easing.cubic)).reduceMotion(ReduceMotion.System)}
      style={{ overflow: 'hidden' }}
    >
      <Controller
        {...{ control }}
        name="pictures"
        render={({ field }) => (
          <MultiImagePicker
            disabled={isAnalyzing}
            onChange={(images) => {
              field.onChange(images);
              onChange?.(images);
            }}
            variant={compactEmptyState && !field.value?.length ? 'compact' : 'default'}
            title={t(compactEmptyState ? 'generalSection.addPhotosCompact' : 'generalSection.addPictures')}
            images={field.value ?? []}
            thumbnailSize={isSmallUI ? 90 : 140}
            // withInlinePicker not needed here since we aren't in a Modal.
          />
        )}
      />
    </Animated.View>
  );
}
